import React, { useState, useEffect } from 'react';
import { getServiceAccount, previewPreloadedAccount } from '../api/activityApi';

// Maps activity type to display service name (without subject prefix)
const SERVICE_NAMES = {
  duolingo: 'Duolingo',
  khan_academy: 'Khan Academy',
  codecombat: 'CodeCombat',
  code_combat_ai: 'CodeCombat AI',
  brilliant: 'Brilliant'
};

/**
 * ServiceAccountSelector - Handles service account selection/creation
 * for external learning services (CodeCombat, Duolingo, Khan Academy)
 *
 * If child already has a service account, shows it with option to change.
 * Guardian can switch between preloaded and custom accounts during activity creation.
 * Otherwise, asks parent to choose between:
 * - Use their own account (custom/guardian)
 * - Use account provided by Powerkid (preloaded)
 */
const ServiceAccountSelector = ({
  childId,
  childUsername,
  activityType,
  activityName,
  csrfToken,
  onAccountReady,
  onBack,
  isTestMode,
  isValidating = false, // True when parent component is validating credentials
  startInChangeMode = false // When true, skip existing account display and show selection options directly
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingAccount, setExistingAccount] = useState(null);
  const [preloadedAvailable, setPreloadedAvailable] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isChangingAccount, setIsChangingAccount] = useState(startInChangeMode); // Show selection options to change existing account
  const [isContinuing, setIsContinuing] = useState(false); // True when user clicked "Continue with this account"
  const [selectedOption, setSelectedOption] = useState(null); // 'preloaded' or 'custom'

  // Check for existing service account on mount
  useEffect(() => {
    checkServiceAccount();
  }, [childId, activityType]);

  const checkServiceAccount = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await getServiceAccount(childId, activityType, csrfToken);

      if (result.exists) {
        // Child already has a service account - show it and proceed
        setExistingAccount(result);
        setPreloadedAvailable(result.preloaded_available);
      } else {
        // No service account, show selection options
        setExistingAccount(null);
        setPreloadedAvailable(result.preloaded_available);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseExistingAccount = () => {
    // Set loading state and proceed with the existing account
    setIsContinuing(true);
    // Pass the existing account's flags so parent can pre-select Super checkbox
    onAccountReady(existingAccount.active_details, false, false, false, existingAccount.flags);
  };

  const handleSelectPreloaded = async () => {
    setIsCreating(true);
    setError(null);

    try {
      // Preview the preloaded account without creating a service account
      // This allows the guardian to switch account types before final submission
      const result = await previewPreloadedAccount(childId, activityType, csrfToken);

      if (result.success) {
        // Proceed with the preloaded account details for validation
        // The actual service account will be created/updated when the activity is submitted
        // Pass isChanging=true if we're changing an existing account
        // Note: onAccountReady will trigger async validation in parent
        // We reset isCreating here; parent's isValidating prop will handle loading state
        setIsCreating(false);
        onAccountReady(result.preloaded_account.details, false, true, isChangingAccount);
      }
    } catch (err) {
      setError(err.message);
      setIsCreating(false);
    }
  };

  const handleSelectCustom = () => {
    // Proceed to credentials form without creating service account yet
    // The service account will be created after validation
    // Pass isChanging=true if we're changing an existing account
    // Always prefill with guardian_account_details if available (parent's previously entered credentials)
    // This ensures parent sees their previously entered credentials even after switching to preloaded and back
    const guardianDetails = existingAccount?.guardian_account_details;
    onAccountReady(guardianDetails || null, true, false, isChangingAccount);
  };

  const handleContinue = () => {
    if (selectedOption === 'preloaded') {
      handleSelectPreloaded();
    } else if (selectedOption === 'custom') {
      handleSelectCustom();
    }
  };

  const handleChangeAccount = () => {
    // Show selection options to change existing account
    setIsChangingAccount(true);
  };

  const handleCancelChange = () => {
    // Go back to showing existing account
    setIsChangingAccount(false);
  };

  // Get service name for account-related text (e.g., "Khan Academy" instead of "Toán (Khan Academy)")
  const serviceName = SERVICE_NAMES[activityType] || activityName;

  // Loading state
  if (isLoading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3 text-muted">Đang kiểm tra tài khoản...</p>
      </div>
    );
  }

  // Show existing account with option to change (unless already in change mode)
  if (existingAccount && !isChangingAccount) {
    const isPowerkidAccount = existingAccount.account_provider === 'powerkid';
    const username = existingAccount.active_details?.username || existingAccount.active_details?.email || 'N/A';

    return (
      <div {...(isTestMode && { 'data-testid': 'service-account-selector' })}>
        {/* Current Account Info */}
        <div
          className="alert alert-success border-success mb-4"
          role="alert"
          {...(isTestMode && { 'data-testid': 'existing-account-display' })}
        >
          <div className="d-flex align-items-start">
            <div className="flex-shrink-0 me-2">
              <i className="bi bi-check-circle-fill fs-4"></i>
            </div>
            <div className="flex-grow-1">
              <h6 className="alert-heading fw-semibold mb-1">
                Đã có tài khoản {serviceName}
              </h6>
              <p className="mb-2 small">
                {childUsername} đã được gán tài khoản {serviceName}.
              </p>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <span className="badge bg-light text-dark">
                  <i className="bi bi-person me-1"></i>
                  {username}
                </span>
                <span className={`badge ${isPowerkidAccount ? 'bg-primary' : 'bg-secondary'}`}>
                  {isPowerkidAccount ? 'Powerkid' : 'Tài khoản riêng'}
                </span>
                <button
                  type="button"
                  className="btn btn-link btn-sm text-success p-0 ms-auto"
                  onClick={handleChangeAccount}
                  {...(isTestMode && { 'data-testid': 'change-account-btn' })}
                >
                  Đổi Tài Khoản
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="alert alert-danger"
            role="alert"
            {...(isTestMode && { 'data-testid': 'service-account-error' })}
          >
            {error}
          </div>
        )}

        {/* Continue Button */}
        <div className="d-flex flex-column gap-3">
          <button
            type="button"
            className="btn btn-primary btn-lg"
            onClick={handleUseExistingAccount}
            disabled={isContinuing || isValidating}
            {...(isTestMode && { 'data-testid': 'use-existing-btn' })}
          >
            {(isContinuing || isValidating) ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Đang xác thực...
              </>
            ) : (
              <>
                <i className="bi bi-arrow-right me-2"></i>
                Tiếp tục với tài khoản này
              </>
            )}
          </button>
        </div>

        {/* Back Button */}
        <div className="mt-4">
          <button
            type="button"
            className="btn btn-link text-secondary p-0"
            onClick={onBack}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Quay Lại Chọn Hoạt Động
          </button>
        </div>
      </div>
    );
  }

  // Show selection options (no existing account OR in change mode)
  return (
    <div {...(isTestMode && { 'data-testid': 'service-account-selector' })}>
      {/* Info Alert - only show when changing account */}
      {isChangingAccount && (
        <div className="alert alert-warning border-warning mb-4" role="alert">
          <div className="d-flex">
            <div className="flex-shrink-0 me-2">
              <i className="bi bi-arrow-repeat"></i>
            </div>
            <div>
              <h6 className="alert-heading fw-semibold mb-1">
                Đổi Tài Khoản
              </h6>
              <p className="mb-0 small">
                Chọn tài khoản mới cho {childUsername}. Tài khoản hiện tại sẽ được thay thế.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          className="alert alert-danger"
          role="alert"
          {...(isTestMode && { 'data-testid': 'service-account-error' })}
        >
          {error}
        </div>
      )}

      {/* Selection Options */}
      <div className="d-flex flex-column gap-3">
        {/* Option 1: Use Preloaded Account (Powerkid provided) */}
        <div
          className={`form-check p-3 border rounded ${selectedOption === 'preloaded' ? 'border-primary bg-light' : ''} ${!preloadedAvailable ? 'opacity-50' : ''}`}
          {...(isTestMode && { 'data-testid': 'option-preloaded' })}
        >
          <input
            className="form-check-input"
            type="radio"
            name="accountOption"
            id="option-preloaded"
            checked={selectedOption === 'preloaded'}
            onChange={() => setSelectedOption('preloaded')}
            disabled={!preloadedAvailable || isCreating}
          />
          <label className={`form-check-label w-100 ${preloadedAvailable ? 'cursor-pointer' : ''}`} htmlFor="option-preloaded">
            <span className="fw-semibold">
              Sử dụng tài khoản Powerkid cung cấp
            </span>
            {preloadedAvailable && (
              <span className="badge bg-success ms-2">Khuyến nghị</span>
            )}
            {!preloadedAvailable && (
              <span className="text-warning small d-block mt-1">
                <i className="bi bi-exclamation-triangle me-1"></i>
                Hiện không còn tài khoản có sẵn
              </span>
            )}
          </label>
        </div>

        {/* Option 2: Use Custom Account */}
        <div
          className={`form-check p-3 border rounded ${selectedOption === 'custom' ? 'border-primary bg-light' : ''}`}
          {...(isTestMode && { 'data-testid': 'option-custom' })}
        >
          <input
            className="form-check-input"
            type="radio"
            name="accountOption"
            id="option-custom"
            checked={selectedOption === 'custom'}
            onChange={() => setSelectedOption('custom')}
            disabled={isCreating}
          />
          <label className="form-check-label w-100 cursor-pointer" htmlFor="option-custom">
            <span className="fw-semibold">Sử dụng tài khoản bạn tự đăng kí</span>
          </label>
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-4">
        <button
          type="button"
          className="btn btn-primary btn-lg w-100"
          onClick={handleContinue}
          disabled={!selectedOption || isCreating || isValidating}
          {...(isTestMode && { 'data-testid': 'continue-btn' })}
        >
          {(isCreating || isValidating) ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Đang xác thực...
            </>
          ) : (
            <>
              Tiếp tục
              <i className="bi bi-arrow-right ms-2"></i>
            </>
          )}
        </button>
      </div>

      {/* Back/Cancel Button */}
      <div className="mt-4">
        {isChangingAccount ? (
          <button
            type="button"
            className="btn btn-link text-secondary p-0"
            onClick={handleCancelChange}
            disabled={isCreating}
          >
            <i className="bi bi-x-circle me-2"></i>
            Hủy Đổi Tài Khoản
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-link text-secondary p-0"
            onClick={onBack}
            disabled={isCreating}
          >
            <i className="bi bi-arrow-left me-2"></i>
            Quay Lại Chọn Hoạt Động
          </button>
        )}
      </div>
    </div>
  );
};

export default ServiceAccountSelector;
